import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/site/ProductCard";
import { MaskLines } from "@/components/site/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("popular");
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/products", { params: { category, q: q || undefined, sort } })
        .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
        .catch(() => setItems([]));
    }, 200);
    return () => clearTimeout(t);
  }, [category, q, sort]);

  const pick = (c) => setParams(c === "All" ? {} : { category: c });

  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-32 pt-12 md:px-10 md:pt-20" data-testid="shop-page">
      <p className="eyebrow text-[var(--terra-deep)]">{items ? `${items.length} products` : "Loading"}</p>
      <MaskLines key={category} as="h1" className="font-display mt-3 text-5xl leading-[1] tracking-tight sm:text-6xl" lines={[{ text: category === "All" ? "The whole edit" : category }]} />
      <div className="mt-10 flex flex-col gap-4 border-y border-[var(--line)] py-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="-mx-1 flex gap-2 overflow-x-auto pb-1" data-lenis-prevent>
          {["All", ...cats].map((c) => (
            <button key={c} onClick={() => pick(c)} className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${c === category ? "bg-[var(--ink)] text-[var(--bone)]" : "border border-[var(--line)] hover:border-[var(--ink)]"}`} data-testid={`category-filter-${c.toLowerCase()}`}>{c}</button>
          ))}
        </div>
        <div className="flex gap-3">
          <label className="flex flex-1 items-center gap-2 rounded-full border border-[var(--line)] px-4 lg:w-64">
            <Search size={16} className="text-[var(--ink-soft)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-[var(--ink-soft)]" data-testid="shop-search-input" />
          </label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-11 w-40 rounded-full bg-transparent" data-testid="shop-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="popular">Most popular</SelectItem>
              <SelectItem value="price_asc">Price: low to high</SelectItem>
              <SelectItem value="price_desc">Price: high to low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {items && items.length === 0 && <p className="py-24 text-center font-display text-3xl" data-testid="shop-empty">No matches. Try another search.</p>}
      <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-12 md:gap-x-6 lg:grid-cols-4">
        {(items || []).map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
      </div>
    </div>
  );
}
